import React from 'react';
import { Button, ButtonProps, Typography, Box, InputLabel, InputBase, FormControl, Stack } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/system';
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { authURL } from './App';

// コンテナ (背景画像: public/background.png)
// 既存コメントは維持しつつ背景を全面表示。可読性確保のため LoginCard は白背景のまま。
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  // 背景画像設定
  backgroundImage: 'url(/background.png)', // CRA/ Vite など public 配下参照
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: '#fff', // 画像読み込み前のフォールバック
});

// ログインカード (半透明 + 背景ぼかし)
const LoginCard = styled('div')({
  // さらに透過 (履歴: 0.82 -> 0.55 -> 0.38 -> 0.25 -> 今回 0.18)
  backgroundColor: 'rgba(255,255,255,0.18)',
  backdropFilter: 'blur(14px) saturate(150%)',
  WebkitBackdropFilter: 'blur(14px) saturate(150%)', // Safari 対応
  // 上パディングを詰める
  padding: '0px 30px 30px',
  // 入力幅を少し広げる
  minWidth: '300px',
  width: '300px',
  borderRadius: '14px',
  boxShadow: '0 4px 28px rgba(0,0,0,0.30)',
  border: '1px solid rgba(255,255,255,0.5)',
  transition: 'background-color 0.25s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.26)'
  }
});

// ラベル
const ThemeLabel = styled(InputLabel)(({ theme }) => ({
  color: 'black',
  fontSize: 20,
  fontWeight: 'normal',
  '&.Mui-focused': {
    color: theme.palette.primary.main,
  }
}));

// 入力フィールド
const ThemeInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
    border: '1px solid #ced4da',
    fontSize: 14,
    width: 'auto%',
    padding: '10px 12px',
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

// ログインフォームデータ
type FormData = {
  username: string;
  password: string;
};

// ログインページ
const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<FormData>();

  // JWT認証
  const getJWT = async (form: FormData) =>{
    // ユーザ名とパスワードを認証APIへ送信
    fetch(`${authURL}/jwt/create/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: form.username,
        password: form.password,
      }),
    })
    .then(res => {
      // 認証成功
      if (res.status === 200) {
        return res.json();
      }
      // 認証失敗
      else if (res.status === 401) {
        alert(`HTTP ${res.status} ${res.statusText}\nUser,Passwordが違います`);
        throw new Error(`HTTP ${res.status} ${res.statusText}`)
      }
      // その他のエラー
      else {
        alert(`HTTP ${res.status} ${res.statusText}\nサーバエラーが発生しました`);
        throw new Error(`HTTP ${res.status} ${res.statusText}`)
      }
    })
    .then((data) => {
      // 認証成功
      //console.log(data)
      // アクセルトークンとリフレッシュトークンをローカルストレージへ保存
      localStorage.setItem('accesstoken', data.access);
      localStorage.setItem('refreshtoken', data.refresh);
      // トップ画面へ遷移
      navigate("/", {replace: true});
    })
    .catch((error) => {
      // エラー
      console.log(error)
    });
  }

  // レンダリング
  return (
    <Container>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
        <Box component="img" src="./logo.png" alt="COVERAGE" sx={{ m: 1, height: "64px", width: "auto" }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          COVERAGE
        </Typography>
      </Box>
      <LoginCard>
        <Box component="form" onSubmit={handleSubmit(getJWT)} sx={{ mt: 5 }}>
          <Stack direction="column" spacing={2} sx={{ justifyContent: "center", alignItems: "flex-start" }}>
            <FormControl variant="standard" fullWidth>
              <ThemeLabel shrink htmlFor="username">ユーザー名</ThemeLabel>
              <ThemeInput placeholder="ユーザー名を入力" defaultValue="" id="username" type="text" {...register('username')}/>
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <ThemeLabel shrink htmlFor="password">パスワード</ThemeLabel>
              <ThemeInput placeholder="パスワードを入力" defaultValue="" id="password" type="password" {...register('password', { required: true })}/>
            </FormControl>
            <Box width="100%" sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2, color: 'white' }} fullWidth>
                ログイン
              </Button>
            </Box>
          </Stack>
        </Box>
      </LoginCard>
    </Container>
  );
}

export default Login;
